import React from 'react';
import { useForm } from 'react-hook-form';
import { UserIcon, EnvelopeIcon, PhoneIcon, UsersIcon } from '@heroicons/react/24/outline';

interface GuestFormData {
  name: string;
  email: string;
  phone: string;
  guests: number;
}

interface GuestInfoProps {
  guestData: GuestFormData | null;
  onSubmit: (data: GuestFormData) => void;
}

const GuestInfo: React.FC<GuestInfoProps> = ({ guestData, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<GuestFormData>({
    defaultValues: guestData || {
      name: '',
      email: '',
      phone: '',
      guests: 1
    }
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">
        Tell us about yourself
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              {...register('name', { required: 'Name is required' })}
              className={`block w-full pl-10 pr-3 py-2 border ${
                errors.name ? 'border-red-300 ring-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors`}
              placeholder="Enter your full name"
            />
          </div>
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className={`block w-full pl-10 pr-3 py-2 border ${
                errors.email ? 'border-red-300 ring-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors`}
              placeholder="Enter your email"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              {...register('phone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[0-9+\-\s()]{10,}$/,
                  message: 'Invalid phone number'
                }
              })}
              className={`block w-full pl-10 pr-3 py-2 border ${
                errors.phone ? 'border-red-300 ring-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors`}
              placeholder="Enter your phone number"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of Guests
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UsersIcon className="h-5 w-5 text-gray-400" />
            </div>
            <select
              {...register('guests', { required: 'Number of guests is required' })}
              className={`block w-full pl-10 pr-3 py-2 border ${
                errors.guests ? 'border-red-300 ring-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors`}
            >
              {[1, 2, 3, 4, 5, 6].map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'Guest' : 'Guests'}
                </option>
              ))}
            </select>
          </div>
          {errors.guests && (
            <p className="mt-1 text-sm text-red-600">{errors.guests.message}</p>
          )}
        </div>

        <div className="mt-6 bg-gray-50 -mx-6 -mb-6 px-6 py-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            By continuing, you agree to our terms and conditions and privacy policy.
          </p>
        </div>
      </form>
    </div>
  );
};

export default GuestInfo;